import React, { useState, useRef, useEffect } from "react";
import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
} from "antd";
import UserService from "../../Services/UserService";
import { convertUTCDate } from "../../utils/convertionFunc";
import { useForm } from "antd/es/form/Form";

function PatientReg({ handleCancel }) {
  const formRef = useRef();
  const [form] = useForm();
  const [dob, setDob] = useState("");
  const [slotChecked, setSlotChecked] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const [disable, setDisable] = useState(false);
  const [fields, setFields] = useState({
    firstname: "",
    lastname: "",
  });

  useEffect(() => {
    getDoctorsList();
  }, []);

  useEffect(() => {
    if (fields.firstname) {
      const formattedFirstname =
        fields.firstname.charAt(0).toUpperCase() +
        fields.firstname.slice(1).toLowerCase();
      form.setFieldsValue({ firstname: formattedFirstname });
    }
    if (fields.lastname) {
      const formattedLastname =
        fields.lastname.charAt(0).toUpperCase() +
        fields.lastname.slice(1).toLowerCase();
      form.setFieldsValue({ lastname: formattedLastname });
    }
  }, [fields]);

  const getDoctorsList = async () => {
    const userPayload = {
      userId: process.env.REACT_APP_SUPERADMIN_USER,
      status: 1,
    };
    try {
      const response = await UserService.getDoctors(userPayload);
      setDoctor(response.data[0]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSlot = () => {
    setSlotChecked(!slotChecked);
  };

  const validatePhoneNumber = (number) => /^\d{10}$/.test(number);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePatientCreate = async () => {
    const { firstname, lastname, gender, email, mobile } =
      formRef.current.getFieldValue();
    setDisable(true);
    if (!firstname || !lastname || !mobile) {
      message.error("Please fill in required fields.");
      setDisable(false);
      return;
    }

    if (firstname && firstname.length < 3) {
      message.error("Please provide min of 3 characters.");
      setDisable(false);
      return;
    }

    if (email && !validateEmail(email)) {
      message.error("Please enter a valid email");
      setDisable(false);
      return;
    }

    if (!validatePhoneNumber(mobile)) {
      message.error("Please enter your 10 digit mobile number");
      setDisable(false);
      return;
    }

    const payload = {
      account_Id: doctor?.account_Id,
      account_Name: null,
      address1: null,
      address2: null,
      bloodGroup: null,
      city: null,
      clinic_Id: doctor?.clinic_Id,
      clinic_Name: null,
      country: null,
      createdBy: doctor?.userId,
      createdDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
      dob: dob,
      email: email,
      firstName: firstname,
      gender,
      lastName: lastname,
      loginId: null,
      loginIdAvailableYN: null,
      maritalStatus: null,
      meetingLink: null,
      mobile,
      modifiedBy: doctor?.userId,
      modifiedDate: convertUTCDate(new Date(), "YYYY-MM-DD HH:mm:ss"),
      parentPatientId: null,
      parentPatientName: null,
      patientId: null,
      patientRegFee: slotChecked ? 500 : 0,
      patientRegFeeYN: slotChecked ? "Y" : "N",
      photo: null,
      photoName: null,
      photoType: null,
      pinCode: null,
      relationCode: null,
      relationDescription: null,
      salutation: null,
      state: null,
      status: 1,
      userId: doctor?.userId,
      userName: doctor?.userName,
      sourceChannel: "Website",
    };
    try {
      await UserService.addPatient(payload);
      setDisable(false);
      // const patientPayload = {
      //     patientId: response.data?.Patientid,
      //     status: 1
      // }
      // const patientResponse = await UserService.getPatient(patientPayload);
      // handlePostPatientCreation(patientResponse.data);
      setSlotChecked(false);
      message.success("Patient successfully registered!");
      handleCancel();
      formRef.current.resetFields();
    } catch (e) {
      setDisable(false);
      console.error(e);
      message.error(e.response.data.message);
    }
  };

  const handleDob = (value, valueString) => {
    setDob(valueString);
  };

  return (
    <div>
      <Form
        form={form}
        autoComplete="off"
        ref={formRef}
        initialValues={{ regfee: 500 }}
      >
        <div className="w-full grid grid-cols-1 p-5 rounded-md gap-4">
          {/* First Name Field */}
          <div className="relative md:w-4/5 w-full">
            <span className="absolute md:text-lg text-base font-medium bg-white px-1 z-10 top-[-0.75rem] left-3 text-[#175b66]">
              First Name <span className="text-[#ef4444]">*</span>
            </span>
            <Form.Item name="firstname" className="w-full">
              <Input
                type="text"
                name="firstname"
                min={3}
                onKeyDown={(event) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                  ];
                  if (
                    !/^[a-zA-Z]$/.test(event.key) &&
                    !allowedKeys.includes(event.key)
                  ) {
                    event.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const formattedValue =
                    value.charAt(0).toUpperCase() +
                    value.slice(1).toLowerCase();
                  setFields((prevState) => ({
                    ...prevState,
                    firstname: formattedValue,
                  }));
                }}
                className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
              />
            </Form.Item>
          </div>

          {/* Last Name Field */}
          <div className="relative md:w-4/5 w-full">
            <span className="absolute md:text-lg text-base font-medium bg-white px-1 z-10 top-[-0.75rem] left-3 text-[#175b66]">
              Last Name <span className="text-[#ef4444]">*</span>
            </span>
            <Form.Item name="lastname" className="w-full">
              <Input
                type="text"
                name="lastname"
                onKeyDown={(event) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                  ];
                  if (
                    !/^[a-zA-Z]$/.test(event.key) &&
                    !allowedKeys.includes(event.key)
                  ) {
                    event.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  const formattedValue =
                    value.charAt(0).toUpperCase() +
                    value.slice(1).toLowerCase();
                  setFields((prevState) => ({
                    ...prevState,
                    lastname: formattedValue,
                  }));
                }}
                className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
              />
            </Form.Item>
          </div>

          {/* Mobile Number Field */}
          <div className="relative md:w-4/5 w-full">
            <span className="absolute md:text-lg text-base font-medium bg-white px-1 z-10 top-[-0.75rem] left-3 text-[#175b66]">
              Mobile Number <span className="text-[#ef4444]">*</span>
            </span>
            <Form.Item name="mobile" className="w-full">
              <Input
                maxLength={10}
                onKeyDown={(event) => {
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                  ];
                  if (
                    !/[0-9]/.test(event.key) &&
                    !allowedKeys.includes(event.key)
                  ) {
                    event.preventDefault(); // Prevent non-numeric and non-control key input
                  }
                }}
                className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
              />
            </Form.Item>
          </div>

          {/* Email Address Field */}
          {/* <div className="relative md:w-4/5 w-full">
                        <span className="absolute md:text-lg text-base font-medium bg-white z-10 top-[-0.75rem] left-3 text-[#175b66]">
                            Email Address (Optional)
                        </span>
                        <Form.Item name="email" className="w-full">
                            <Input
                                type="email"
                                name="email"
                                className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
                            />
                        </Form.Item>
                    </div> */}

          {/* Date of Birth Field */}
          {/* <div className="relative md:w-4/5 w-full">
                        <span className="absolute md:text-lg text-base font-medium bg-white z-10 top-[-0.75rem] left-3 text-[#175b66]">
                            Date of Birth
                        </span>
                        <Form.Item name="dob" className="w-full">
                            <DatePicker
                                format="MM/DD/YYYY"
                                className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
                                onChange={handleDob}
                                disabledDate={(current) => current && current.valueOf() > Date.now()}
                            />
                        </Form.Item>
                    </div> */}

          {/* Gender Field */}
          <Form.Item name="gender" className="md:w-4/5 w-full">
            <div>
              <Radio.Group className="flex gap-4">
                <Radio className="text-[#F49D15] font-medium" value="M">
                  Male
                </Radio>
                <Radio className="text-[#F49D15] font-medium" value="F">
                  Female
                </Radio>
              </Radio.Group>
            </div>
          </Form.Item>

          {/* Registration Fee Checkbox */}

          {/*
                   <div className="md:w-4/5 w-full">
                        <span className="md:text-lg text-base font-medium bg-white z-10 top-[-0.75rem] left-3 text-[#175b66]">
                            Would you like to register
                        </span>
                        <div className="flex items-center gap-2">
                            <Checkbox checked={slotChecked} onChange={handleSlot}>
                                {slotChecked ? 'Yes' : 'No'}
                            </Checkbox>
                        </div>
                    </div>
                    \*} 

                    {/* Registration Fee Input Field */}
          {slotChecked && (
            <div className="relative md:w-4/5 w-full mt-4">
              <span className="absolute md:text-lg text-base font-medium bg-white z-10 top-[-0.75rem] left-3 text-[#175b66]">
                Registration Fee
              </span>
              <Form.Item name="regfee" className="w-full">
                <InputNumber
                  disabled={true}
                  controls={false}
                  min={0}
                  className="w-full md:h-12 h-10 border border-[#7FE8EE] p-1 rounded-lg outline-none"
                />
              </Form.Item>
            </div>
          )}
        </div>
      </Form>

      {/* Buttons */}
      <div className="flex justify-end items-center p-2 gap-3">
        <button
          onClick={handleCancel}
          className="md:min-w-28 w-[6rem] md:w-1/6 md:p-3 p-1 bg-white rounded-lg border text-primaryColor border-primaryColor font-semibold custom-button-shadow"
        >
          Cancel
        </button>
        <button
          onClick={handlePatientCreate}
          disabled={disable}
          className="md:min-w-28 w-[6rem] md:w-1/6 md:p-3 p-1 bg-primaryColor text-white rounded-lg font-semibold disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default PatientReg;
